<template>
  <scrollable-container>
    <placeholder
      v-show="isTableAwaiting"
      :text="text"
      @buttonClick="buildReport"
    />
    <loader v-show="isTableLoading" />
    <container
      v-show="isTableReady"
      id="printable"
      :class="[{ [$style.message]: isTableAwaiting }, $style.scrollableContent]"
    >
      <div
        v-if="tableData.length === 0"
        :class="$style.emptyResult"
      >
        {{ $t('reports.noDataForSpecifiedPeriod') }}
      </div>

      <div
        v-for="(user, index) in tableData"
        :key="user.name"
        :class="$style.user"
      >
        <div
          :class="$style.userStickyToggle"
          @click="toggle(user)"
        >
          <div :class="[$style.expandButton, { [$style.expanded]: user.isExpanded }]" />
          <div :class="$style.name">
            {{ user.name }}
          </div>
        </div>

        <productivity-table
          v-for="date in user.dates"
          v-show="user.isExpanded"
          :key="user.name + date.date + index"
          :class="$style.userData"
          :date="date"
        />
      </div>
    </container>
  </scrollable-container>
</template>

<script>
  import ScrollableContainer from '@/components/common/blocks/scrollable-container.vue';
  import Container from '@/components/common/blocks/container.vue';
  import Placeholder from '@/components/common/blocks/report-placeholder.vue';
  import { getProductivityReport } from '@/api/methods/reports/users/index';
  import Loader from '@/components/common/blocks/loader.vue';
  import { getDataField } from '@/views/reports/users/worktime/table-field';
  import { REPORT_ENUM } from '@/assets/scripts/reportEnum';
  import ProductivityTable from './productivity-table.vue';

  export default {
    components: {
      ScrollableContainer,
      Container,
      Placeholder,
      ProductivityTable,
      Loader,
    },
    data() {
      return {
        name: this.$t('reports.workProductivityReport'),
        tableData: [],
        computersArray: [],
        text: this.$t('reports.pressButtonToGenerateReport'),
        columns: [
          { name: this.$t('reports.columns.enabled'), field: 'workTime' },
          { name: this.$t('reports.columns.activeTime'), field: 'activeTime' },
          { name: this.$t('reports.columns.idleTime'), field: 'idleTime' },
          { name: this.$t('reports.columns.activePercent'), field: 'activePercent' },
          { name: this.$t('reports.columns.dayStart'), field: 'dayStart' },
          { name: this.$t('reports.columns.dayFinish'), field: 'dayFinish' },
          { name: this.$t('reports.columns.isWorkingDay'), field: 'isWorkingDay' },
          { name: this.$t('reports.columns.plan'), field: 'plan' },
          { name: this.$t('reports.columns.overwork'), field: 'overwork' },
          { name: this.$t('reports.columns.productiveTime'), field: 'productiveTime' },
          { name: this.$t('reports.columns.unproductiveTime'), field: 'unproductiveTime' },
          { name: this.$t('reports.columns.neutralTime'), field: 'neutralTime' },
        ],
      };
    },
    computed: {
      isTableReady() {
        return this.$store.getters['report/getStateTable'] === REPORT_ENUM.TABLE_READY;
      },
      isTableAwaiting() {
        return this.$store.getters['report/getStateTable'] === REPORT_ENUM.REQUEST_AWAITING;
      },
      isTableLoading() {
        return this.$store.getters['report/getStateTable'] === REPORT_ENUM.TABLE_LOADING;
      },
      dateRange() {
        return this.$store.getters['filters/formattedDateRange'];
      },
      checkedComputers() {
        return this.$store.getters['pageSpecificData/checkedComputersList'];
      },
      computersTree() {
        return this.$store.getters['pageSpecificData/computersTree'];
      },
      isWorktimeOnly() {
        return this.$store.getters['filters/isWorktimeOnly'];
      },
    },
    watch: {
      dateRange(oldValue, newValue) {
        if (
          oldValue.dateFrom.toString() !== newValue.dateFrom.toString() ||
          oldValue.dateTo.toString() !== newValue.dateTo.toString()
        ) {
          this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
        }
      },
      checkedComputers(oldArray, newArray) {
        if (oldArray !== newArray) {
          this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
        }
      },
      isWorktimeOnly(oldValue, newValue) {
        if (oldValue !== newValue) {
          this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
        }
      },
    },
    mounted() {
      this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
    },
    methods: {
      toggle(user) {
        user.isExpanded = !user.isExpanded;
      },
      getCheckedComputersFromTree(node) {
        if (node.type === 'computer' && node.checked) {
          this.computersArray.push(node);
        }
        if (node.type === 'folder') {
          node.children.forEach((child) => this.getCheckedComputersFromTree(child));
        }
      },
      buildReport() {
        this.$store.commit('report/setStateTable', REPORT_ENUM.TABLE_LOADING);
        this.tableData = [];
        this.computersArray = [];
        let workTimeOnly = 0;

        if (this.isWorktimeOnly) workTimeOnly = 1;

        if (this.computersTree) {
          this.computersTree.forEach((node) => this.getCheckedComputersFromTree(node));
        }

        if (this.dateRange.dateFrom && this.dateRange.dateTo && this.computersArray) {
          this.promise = getProductivityReport(
            this.computersArray,
            this.dateRange.dateFrom,
            this.dateRange.dateTo,
            workTimeOnly,
          ).then(({ data }) => {
            if (data) {
              this.tableData = data.result;

              this.$store.commit('report/setStateTable', REPORT_ENUM.TABLE_READY);

              const exportArray = [];

              for (let i = 0; i < data.result.length; i++) {
                const { name } = data.result[i];
                for (let x = 0; x < data.result[i].dates.length; x++) {
                  const { date } = data.result[i].dates[x];
                  const resObj = { Пользователь: name, Дата: date };
                  for (let j = 0; j < this.columns.length; j++) {
                    resObj[this.columns[j].name] = getDataField(
                      data.result[i].dates[x][this.columns[j].field],
                      this.columns[j].field,
                    );
                  }
                  exportArray.push(resObj);
                }
              }

              this.$store.commit('exportData/setProductivity', { name: this.name, data: exportArray });
            } else {
              this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
            }
          });
        }
        return this.tableData;
      },
    },
  };
</script>

<style lang="scss" module>
  ::-webkit-scrollbar {
    width: 4px;
  }

  ::-webkit-scrollbar-track {
    background: white;
  }

  ::-webkit-scrollbar-thumb {
    background: $branchColor;
    border-radius: 2px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: darkgray;
  }

  .scrollableContent {
    margin: 0 8px 0 0;
    padding: 0 12px 0 20px;
    box-shadow: $shadow;
    flex: 1;
  }

  .message {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .userData {
    margin-left: 30px;
    position: relative;
  }

  .name {
    font-weight: 500;
    display: inline-flex;
    background-color: #f5f5f5;
    width: 100%;
    align-items: center;
    padding: 5px 10px;
    margin-right: 12px;
    font-size: 14px;
  }

  .userStickyToggle {
    position: sticky;
    z-index: 3;
    top: -1px;
    width: 100%;
    line-height: 14px;
    cursor: pointer;
    margin-left: 12px;
    align-items: center;
    display: inline-flex;
  }

  .expandButton {
    flex-shrink: 0;
    margin-left: -27px;
    margin-right: 11px;
    width: 14px;
    height: 14px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border: 1px solid $branchColor;
    background: $white;
    z-index: 2;

    &::before,
    &::after {
      content: '';
      background-color: $blue;
      width: 8px;
      height: 2px;
      position: absolute;
      transition: 0.25s ease-out;
    }

    &::before {
      transform: rotate(90deg);
    }

    &::after {
      transform: rotate(180deg);
    }

    &.expanded {
      &::before {
        transform: rotate(0);
      }

      &::after {
        transform: rotate(0);
      }
    }
  }

  .user {
    margin: 5px;
  }

  .emptyResult {
    display: flex;
    justify-content: center;
    margin-top: 20px;
  }
</style>
